import {CanvasService} from "features/apartmentViewer/ApartmentViewer/services/CanvasService";
import {TimeService} from "features/apartmentViewer/ApartmentViewer/services/TimeService";
import {LinearSRGBColorSpace, WebGLRenderer} from "three";
import {CameraHandler} from "features/apartmentViewer/ApartmentViewer/services/CameraHandler";
import {EventManager} from "features/apartmentViewer/ApartmentViewer/services/EventManager";
import {PostProcessingRenderer} from "features/apartmentViewer/ApartmentViewer/renderers/PostProcessingRenderer";
import {MarkerService} from "features/apartmentViewer/ApartmentViewer/containers/MarkerService";
import {MarkerLayer} from "features/apartmentViewer/ApartmentViewer/layers/MarkerLayer";
import {ArrowLayer} from "features/apartmentViewer/ApartmentViewer/layers/ArrowLayer";
import {EmptyLayer} from "features/apartmentViewer/ApartmentViewer/layers/EmptyLayer";
import {TextureHelper} from "features/apartmentViewer/ApartmentViewer/helpers/TextureHelper";
import {HDRTextureProcessor} from "features/apartmentViewer/ApartmentViewer/services/HDRTextureProcessor";
import {DepthManager} from "features/apartmentViewer/ApartmentViewer/services/DepthManager";
import {PanoramaModel} from "features/apartmentViewer/ApartmentViewer/models/PanoramaModel";
import {PanoramaLayer} from "features/apartmentViewer/ApartmentViewer/layers/PanoramaLayer";
import {PanoramaManager} from "features/apartmentViewer/ApartmentViewer/services/PanoramaManager";
import {LayerContainer} from "features/apartmentViewer/ApartmentViewer/containers/LayerContainer";
import {UserNavigationContainer} from "features/apartmentViewer/ApartmentViewer/containers/UserNavigationContainer";
import {ApartmentViewer} from "features/apartmentViewer/ApartmentViewer/ApartmentViewer";
import {SiteModel} from "features/apartmentViewer/ApartmentViewer/models/SiteModel";
import {ExposureManager} from "features/apartmentViewer/ApartmentViewer/services/ExposureManager";
import {ApartmentViewerFacade} from "features/apartmentViewer/ApartmentViewer/ApartmentViewerFacade";
import {ApartmentViewerSettings} from "features/apartmentViewer/ApartmentViewer/ApartmentViewerSettings";
import {LayerManager} from "features/apartmentViewer/ApartmentViewer/services/LayerManager";

export class ApartmentViewerFactory {
    static async createApartmentViewerFacade(canvas: HTMLCanvasElement, siteData: SiteModel, isMobile: boolean): Promise<ApartmentViewerFacade> {
        const canvasService = new CanvasService(canvas);
        const timeService = new TimeService();
        const settings = new ApartmentViewerSettings(isMobile);

        const renderer = this.createRenderer(canvasService);
        const cameraHandler = new CameraHandler(timeService, canvasService);
        const eventManager = new EventManager(canvasService, cameraHandler);

        const exposureManager = new ExposureManager(renderer);
        const markerContainer = await this.createMarkerContainer(siteData);
        const layerManager = new LayerManager(timeService);

        const postProcessingRenderer = new PostProcessingRenderer(timeService, cameraHandler, renderer, canvasService, exposureManager, layerManager);

        const [arrowLayer, markerLayer] = this.createUserNavigationLayers(canvasService, eventManager, renderer, cameraHandler, markerContainer, settings, timeService);
        const baseLayer = this.createBaseLayer(canvasService, renderer, cameraHandler);
        const [panoramaModels, panoramaLayers] = this.createPanoramaComponents(renderer, cameraHandler, siteData, canvasService, settings);

        const panoramaManager = new PanoramaManager(cameraHandler, postProcessingRenderer, markerLayer, panoramaModels, panoramaLayers, settings);
        const baseLayerContainer = this.createBaseLayerContainer(baseLayer);
        const userNavigationLayerContainer = this.createUserNavigationLayerContainer(markerLayer, arrowLayer);
        const userNavigationContainer = new UserNavigationContainer(cameraHandler, eventManager, arrowLayer, markerLayer);


        markerContainer.panoramaManager = panoramaManager;

        const apartmentViewer = new ApartmentViewer(
            panoramaManager,
            timeService,
            eventManager,
            cameraHandler,
            postProcessingRenderer,
            userNavigationContainer,
            baseLayerContainer,
            userNavigationLayerContainer,
        );

        return new ApartmentViewerFacade(
            apartmentViewer,
            siteData,
            postProcessingRenderer,
            panoramaManager,
            markerContainer,
            canvasService,
            settings,
            cameraHandler,
        );
    }

    private static createRenderer(canvasService: CanvasService): WebGLRenderer {
        const renderer = new WebGLRenderer({
            canvas: canvasService.getCanvas(),
            preserveDrawingBuffer: true,
            antialias: true
        });
        renderer.outputColorSpace = LinearSRGBColorSpace;
        const rendererSize = canvasService.getRendererSize();
        renderer.setSize(rendererSize.width, rendererSize.height);
        renderer.setPixelRatio( window.devicePixelRatio );

        return renderer;
    }

    private static async createMarkerContainer(siteData: SiteModel): Promise<MarkerService> {
        const markerContainer = new MarkerService();

        if (!siteData.currentApartment) {
            throw new Error("currentApartment is undefined in siteData.");
        }
        await markerContainer.addMarkers(siteData.currentApartment);
        return markerContainer;
    }

    private static createUserNavigationLayers(
        canvasService: CanvasService,
        eventManager: EventManager,
        renderer: WebGLRenderer,
        cameraHandler: CameraHandler,
        markerService: MarkerService,
        settings: ApartmentViewerSettings,
        timeService: TimeService,
    ): [ArrowLayer, MarkerLayer] {
        const markerLayer = new MarkerLayer(canvasService, markerService, eventManager, cameraHandler, renderer, settings);
        const arrowLayer = new ArrowLayer(canvasService, eventManager, renderer, cameraHandler, settings, timeService);
        return [arrowLayer, markerLayer];
    }

    private static createBaseLayer(canvasService: CanvasService, renderer: WebGLRenderer, cameraHandler: CameraHandler): EmptyLayer {
        return new EmptyLayer(canvasService, renderer, cameraHandler);
    }

    private static createPanoramaComponents(renderer: WebGLRenderer, cameraHandler: CameraHandler, siteModel: SiteModel, canvasService: CanvasService, settings: ApartmentViewerSettings): [PanoramaModel[], PanoramaLayer[]] {
        const textureHelper = new TextureHelper(renderer);
        const hdrTextureProcessor = new HDRTextureProcessor(textureHelper, siteModel, settings, renderer);
        const depthManager = new DepthManager(textureHelper, renderer);
        const panoramaModels = [new PanoramaModel(depthManager, hdrTextureProcessor), new PanoramaModel(depthManager, hdrTextureProcessor)];
        const panoramaLayers = [new PanoramaLayer(canvasService, renderer, cameraHandler), new PanoramaLayer(canvasService, renderer, cameraHandler)];
        return [panoramaModels, panoramaLayers];
    }

    private static createBaseLayerContainer(baseLayer: EmptyLayer): LayerContainer {
        const baseLayerContainer = new LayerContainer();
        baseLayerContainer.addLayer(baseLayer);
        return baseLayerContainer;
    }

    private static createUserNavigationLayerContainer(markerLayer: MarkerLayer, arrowLayer: ArrowLayer): LayerContainer {
        const userNavigationLayerContainer = new LayerContainer();
        userNavigationLayerContainer.addLayer(markerLayer);
        userNavigationLayerContainer.addLayer(arrowLayer);
        return userNavigationLayerContainer;
    }
}

